import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  DialogContentText,
  TextField,
  IconButton,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import DialogBasic from './DialogBasic';
import DetailResultDialog from './DetailResultDialog';
import Commons from '../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * 처리 내역 히스토리 다이얼로그 컴포넌트
 * 
 * @param {boolean} open - 다이얼로그 열림 여부
 * @param {Function} onClose - 다이얼로그 닫기 핸들러
 */
function OperationHistoryDialog({ open = false, onClose = () => {} }) {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuFilter, setMenuFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // 로컬스토리지에서 히스토리 불러오기
  const loadHistory = () => {
    try {
      const historyKey = 'operation_history';
      const loaded = Commons.Storages('get', historyKey) || [];
      setHistory(loaded);
    } catch (error) {
      console.error('[OperationHistoryDialog] 히스토리 로드 실패:', error);
      setHistory([]);
    }
  };

  // 다이얼로그 열릴 때 히스토리 로드
  useEffect(() => {
    if (open) {
      loadHistory();
    }
  }, [open]);

  // 필터링된 히스토리 계산
  useEffect(() => {
    let filtered = [...history];

    // 검색어 필터
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        // menuId 검색
        const menuIdMatch =
          item.menuId && String(item.menuId).toLowerCase().includes(term);

        // summary 검색
        const summaryMatch =
          item.summary && String(item.summary).toLowerCase().includes(term);

        // detailData 검색 (배열인 경우만)
        let detailDataMatch = false;
        if (Array.isArray(item.detailData) && item.detailData.length > 0) {
          detailDataMatch = item.detailData.some((d) => {
            if (!d || typeof d !== 'object') return false;
            const messageMatch =
              d.message && String(d.message).toLowerCase().includes(term);
            const errorMsgMatch =
              d.errorMsg && String(d.errorMsg).toLowerCase().includes(term);
            const statusTypeMatch =
              d.statusType && String(d.statusType).toLowerCase().includes(term);
            return messageMatch || errorMsgMatch || statusTypeMatch;
          });
        }

        return menuIdMatch || summaryMatch || detailDataMatch;
      });
    }

    // 메뉴 필터
    if (menuFilter !== 'all') {
      filtered = filtered.filter((item) => item.menuId === menuFilter);
    }

    // 상태 필터
    if (statusFilter === 'success') {
      filtered = filtered.filter((item) => item.failCount === 0);
    } else if (statusFilter === 'fail') {
      filtered = filtered.filter((item) => item.failCount > 0);
    }

    // 최신순 정렬
    filtered.sort((a, b) => (b.id || 0) - (a.id || 0));

    setFilteredHistory(filtered);
  }, [history, searchTerm, menuFilter, statusFilter]);

  // 고유 메뉴 ID 목록 추출
  const uniqueMenuIds = useMemo(() => {
    const menuSet = new Set(history.map((item) => item.menuId).filter(Boolean));
    return Array.from(menuSet).sort();
  }, [history]);

  // 개별 항목 삭제
  const handleDelete = (id, e) => {
    e.stopPropagation();
    try {
      const historyKey = 'operation_history';
      const updated = history.filter((item) => item.id !== id);
      Commons.Storages('set', historyKey, updated);
      loadHistory();
    } catch (error) {
      console.error('[OperationHistoryDialog] 삭제 실패:', error);
    }
  };

  // 전체 삭제
  const handleClearAll = () => {
    if (window.confirm('모든 처리 내역을 삭제하시겠습니까?')) {
      try {
        const historyKey = 'operation_history';
        Commons.Storages('set', historyKey, []);
        loadHistory();
      } catch (error) {
        console.error('[OperationHistoryDialog] 전체 삭제 실패:', error);
      }
    }
  };

  // 상세 내역 보기
  const handleViewDetail = (item) => {
    setSelectedDetail(item.detailData);
    setDetailDialogOpen(true);
  };

  // 날짜 포맷
  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return dayjs(isoString).format('YYYY-MM-DD HH:mm:ss');
  };

  const renderContent = () => {
    return (
      <Box sx={{ mt: 2 }}>
        {/* 필터 영역 */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            placeholder="검색 (메뉴, 요약, 메시지)"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>메뉴</InputLabel>
            <Select
              value={menuFilter}
              label="메뉴"
              onChange={(e) => setMenuFilter(e.target.value)}
            >
              <MenuItem value="all">전체</MenuItem>
              {uniqueMenuIds.map((menuId) => (
                <MenuItem key={menuId} value={menuId}>
                  {menuId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>상태</InputLabel>
            <Select
              value={statusFilter}
              label="상태"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="success">성공만</MenuItem>
              <MenuItem value="fail">실패 포함</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="새로고침">
            <IconButton onClick={loadHistory} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {history.length > 0 && (
            <Tooltip title="전체 삭제">
              <IconButton onClick={handleClearAll} size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* 히스토리 테이블 또는 빈 메시지 */}
        {filteredHistory.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <DialogContentText>
              {history.length === 0
                ? '저장된 처리 내역이 없습니다.'
                : '검색 조건에 맞는 처리 내역이 없습니다.'}
            </DialogContentText>
          </Box>
        ) : (
          <Paper sx={{ mt: 2, maxHeight: '60vh', overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>
                    처리 시간
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>
                    메뉴
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>
                    요약
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>
                    결과
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>
                    통계
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 'bold', width: '15%', textAlign: 'center' }}
                  >
                    작업
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={() => handleViewDetail(item)}
                  >
                    <TableCell>{formatDate(item.timestamp)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.menuId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.summary}</Typography>
                    </TableCell>
                    <TableCell>
                      {item.failCount === 0 ? (
                        <Chip
                          label="성공"
                          color="success"
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : (
                        <Chip
                          label="실패 포함"
                          color="error"
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        성공: <strong>{item.successCount}</strong> / 실패:{' '}
                        <strong style={{ color: item.failCount > 0 ? '#d32f2f' : 'inherit' }}>
                          {item.failCount}
                        </strong>
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="상세 보기">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetail(item);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => handleDelete(item.id, e)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* 통계 정보 */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            전체: {history.length}건 | 표시: {filteredHistory.length}건
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <DialogBasic
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        title="처리 내역 히스토리"
        closeButtonName="닫기"
        useCloseButton={true}
        useSaveButton={false}
        content={renderContent()}
      />
      <DetailResultDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedDetail(null);
        }}
        detailData={selectedDetail || []}
        title="처리 상세 내역"
      />
    </>
  );
}

export default React.memo(OperationHistoryDialog);

